import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import {
  EyeIcon,
  XCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ShoppingCartIcon,
} from '@heroicons/react/24/outline';
import OrderCancelModal from '../components/OrderCancelModal';
import PageHeader from '../components/ui/PageHeader';

// Status color and label mapping
const STATUS_CONFIG = {
  DRAFT: { label: 'Draft', color: 'bg-slate-100 text-slate-800 border-slate-300' },
  PENDING: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  AWAITING_PAYMENT: {
    label: 'Awaiting Payment',
    color: 'bg-amber-100 text-amber-800 border-amber-300',
  },
  PAYMENT_FAILED: { label: 'Payment Failed', color: 'bg-red-100 text-red-800 border-red-300' },
  PAID: { label: 'Paid', color: 'bg-green-100 text-green-800 border-green-300' },
  PROCESSING: { label: 'Processing', color: 'bg-blue-100 text-blue-800 border-blue-300' },
  PARTIALLY_SHIPPED: {
    label: 'Partially Shipped',
    color: 'bg-indigo-100 text-indigo-800 border-indigo-300',
  },
  SHIPPED: { label: 'Shipped', color: 'bg-purple-100 text-purple-800 border-purple-300' },
  DELIVERED: { label: 'Delivered', color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  CANCELLED: { label: 'Cancelled', color: 'bg-red-100 text-red-800 border-red-300' },
  REFUNDED: { label: 'Refunded', color: 'bg-gray-100 text-gray-800 border-gray-300' },
  DISPUTED: { label: 'Disputed', color: 'bg-orange-100 text-orange-800 border-orange-300' },
  FAILED: { label: 'Failed', color: 'bg-red-100 text-red-800 border-red-300' },
};

const DEFAULT_STATUS_CONFIG = {
  label: 'Unknown',
  color: 'bg-gray-100 text-gray-800 border-gray-300',
};

const ITEMS_PER_PAGE = 20;

export default function Orders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    total: 0,
    limit: ITEMS_PER_PAGE,
    offset: 0,
  });
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
  });
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const params = {
        limit: pagination.limit,
        offset: pagination.offset,
      };

      if (filters.status !== 'all') {
        params.status = filters.status;
      }

      const response = await api.get('/auth/payments/orders', { params });
      const data = response.data.data || response.data;

      setOrders(data.orders || []);
      setPagination((prev) => ({
        ...prev,
        total: data.total || 0,
      }));
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  }, [pagination.limit, pagination.offset, filters.status]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Reset pagination when filters change
  useEffect(() => {
    setPagination((prev) => ({ ...prev, offset: 0 }));
  }, [filters.status]);

  const handleViewDetails = (orderId) => {
    navigate(`/orders/${orderId}`);
  };

  const handleCancelClick = (order) => {
    setSelectedOrder(order);
    setShowCancelModal(true);
  };

  const handleCancelConfirm = async (reason) => {
    if (!selectedOrder) return;

    try {
      setCancelLoading(true);
      await api.post(`/auth/payments/orders/${selectedOrder.id}/cancel`, { reason });
      setShowCancelModal(false);
      setSelectedOrder(null);
      fetchOrders();
    } catch (err) {
      console.error('Error cancelling order:', err);
      const errorMessage =
        err.response?.data?.message || 'Failed to cancel order';
      alert(errorMessage);
    } finally {
      setCancelLoading(false);
    }
  };

  const handlePageChange = (newOffset) => {
    setPagination((prev) => ({ ...prev, offset: newOffset }));
  };

  const formatCurrency = (cents) => {
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR',
    }).format(cents / 100);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-PT', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || DEFAULT_STATUS_CONFIG;
    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${config.color}`}
      >
        {config.label}
      </span>
    );
  };

  const canCancelOrder = (order) => {
    // Only pending orders can be cancelled
    return order.status === 'pending';
  };

  // Filter orders by search term (client-side)
  const filteredOrders = orders.filter((order) => {
    if (!filters.search) return true;
    const search = filters.search.toLowerCase();
    return (
      order.orderNumber?.toLowerCase().includes(search) ||
      order.id?.toLowerCase().includes(search)
    );
  });

  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;
  const totalPages = Math.ceil(pagination.total / pagination.limit);

  if (loading && orders.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <PageHeader
        title="Order Management"
        description={`${pagination.total} ${pagination.total === 1 ? 'order' : 'orders'} total`}
      />

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-6 border border-slate-200">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search by order number..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="relative">
            <FunnelIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="pl-10 pr-8 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white appearance-none cursor-pointer"
            >
              <option value="all">All Statuses</option>
              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                <option key={key} value={key}>
                  {config.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Orders Table */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">
                  Order Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">
                  Total
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                    <div className="flex flex-col items-center">
                      <ShoppingCartIcon className="h-12 w-12 text-slate-400 mb-2" />
                      <p className="text-sm">No orders found</p>
                      {filters.status !== 'all' && (
                        <button
                          onClick={() => setFilters({ ...filters, status: 'all' })}
                          className="mt-2 text-blue-600 hover:text-blue-800 text-sm"
                        >
                          Clear filters
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                filteredOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-slate-900">
                        {order.orderNumber}
                      </div>
                      <div className="text-xs text-slate-500">{order.id}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(order.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-slate-900">
                        {formatCurrency(order.totalInCents)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-600">
                        {formatDate(order.createdAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                      <button
                        onClick={() => handleViewDetails(order.id)}
                        className="inline-flex items-center px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-md transition-colors"
                      >
                        <EyeIcon className="h-4 w-4 mr-1" />
                        View
                      </button>
                      {canCancelOrder(order) && (
                        <button
                          onClick={() => handleCancelClick(order)}
                          className="inline-flex items-center px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-700 rounded-md transition-colors"
                        >
                          <XCircleIcon className="h-4 w-4 mr-1" />
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-slate-50 px-6 py-3 border-t border-slate-200 flex items-center justify-between">
            <div className="text-sm text-slate-700">
              Showing{' '}
              <span className="font-medium">{pagination.offset + 1}</span> to{' '}
              <span className="font-medium">
                {Math.min(pagination.offset + pagination.limit, pagination.total)}
              </span>{' '}
              of <span className="font-medium">{pagination.total}</span> results
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(pagination.offset - pagination.limit)}
                disabled={pagination.offset === 0}
                className="inline-flex items-center px-3 py-1.5 border border-slate-300 rounded-md text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeftIcon className="h-4 w-4 mr-1" />
                Previous
              </button>
              <span className="text-sm text-slate-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(pagination.offset + pagination.limit)}
                disabled={currentPage >= totalPages}
                className="inline-flex items-center px-3 py-1.5 border border-slate-300 rounded-md text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
                <ChevronRightIcon className="h-4 w-4 ml-1" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Cancel Modal */}
      {showCancelModal && selectedOrder && (
        <OrderCancelModal
          order={selectedOrder}
          onConfirm={handleCancelConfirm}
          onCancel={() => {
            setShowCancelModal(false);
            setSelectedOrder(null);
          }}
          loading={cancelLoading}
        />
      )}
    </div>
  );
}
